import {
  ProjectDetailsType,
  QuoteRecipientInputType,
  QuoteRecipientsType
} from 'data/types';
import { produce } from 'immer';

const EMAIL_PATTERN = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;
const TEN_DIGIT_PHONE_PATTERN = /^[2-9][0-8]\d[2-9]\d{6}$/;

export const emptyQuoteRecipients: QuoteRecipientsType = {
  email: '',
  phoneNumber: ''
};

export function isValidEmail(email: string): boolean {
  return EMAIL_PATTERN.test(email.trim());
}

export function normalizePhoneDigits(phoneNumber: string): string {
  const digits = phoneNumber.replace(/\D/g, '');
  return digits.length === 11 && digits.startsWith('1')
    ? digits.slice(1)
    : digits;
}

export function isValidPhoneNumber(phoneNumber: string): boolean {
  return TEN_DIGIT_PHONE_PATTERN.test(normalizePhoneDigits(phoneNumber));
}

export function validateQuoteRecipientDraft({
  email,
  phoneNumber
}: QuoteRecipientsType): string | null {
  if (email.trim().length > 0) {
    return 'Select "New Email" to use the email address you typed';
  }
  if (phoneNumber.trim().length > 0) {
    return 'Select "New Number" to use the mobile number you typed';
  }
  return null;
}

export function validateQuoteRecipients({
  email,
  phoneNumber
}: QuoteRecipientsType): string | null {
  const hasEmail = email.trim().length > 0;
  const hasPhoneNumber = phoneNumber.trim().length > 0;

  if (!hasEmail && !hasPhoneNumber) {
    return 'Enter an email address or a mobile number';
  }
  if (hasEmail && !isValidEmail(email)) {
    return 'Please enter a valid email address';
  }
  if (hasPhoneNumber && !isValidPhoneNumber(phoneNumber)) {
    return 'Please enter a valid phone number';
  }
  return null;
}

export function buildQuoteRecipientInput({
  email,
  phoneNumber
}: QuoteRecipientsType): QuoteRecipientInputType {
  const trimmedEmail = email.trim();
  const phoneDigits = normalizePhoneDigits(phoneNumber);

  return {
    ...(trimmedEmail ? { emailAddress: trimmedEmail } : {}),
    ...(phoneDigits ? { phoneNumber: phoneDigits } : {})
  };
}

export function withRememberedQuoteRecipients(
  projectDetails: ProjectDetailsType | null,
  { email, phoneNumber }: QuoteRecipientsType
): ProjectDetailsType | null {
  if (!projectDetails) {
    return projectDetails;
  }

  const trimmedEmail = email.trim();
  const trimmedPhoneNumber = phoneNumber.trim();

  return produce(projectDetails, (draft) => {
    if (trimmedEmail && !draft.customerEmails.includes(trimmedEmail)) {
      draft.customerEmails.push(trimmedEmail);
    }
    if (
      trimmedPhoneNumber &&
      !draft.customerPhoneNumbers.includes(trimmedPhoneNumber)
    ) {
      draft.customerPhoneNumbers.push(trimmedPhoneNumber);
    }
  });
}
